import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useMessage } from '../hooks/useMessage'
import { formatDateFull } from '../utils/formatters'
import { VALIDATION } from '../constants'
import type { User } from '../types'
import './Users.css'

interface UsersResponse {
  success: boolean
  message?: string
  users?: User[]
  user?: User
}

interface FormData {
  username: string
  password: string
  role: string
}

export function Users() {
  const { user } = useAuth()
  const { message, showError, showSuccess } = useMessage()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    username: '',
    password: '',
    role: 'guest',
  })
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null)
  const [newPassword, setNewPassword] = useState('')
  const [resetting, setResetting] = useState(false)

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/users')
      const data: UsersResponse = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Could not fetch users')
      }

      setUsers(data.users || [])
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not fetch users')
    } finally {
      setLoading(false)
    }
  }, [showError])

  useEffect(() => {
    if (!user) return
    fetchUsers()
  }, [user, fetchUsers])

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      role: 'guest',
    })
    setShowForm(false)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Client-side validation
    if (!formData.username.trim()) {
      showError('Username is required')
      return
    }

    if (!VALIDATION.USERNAME_PATTERN.test(formData.username)) {
      showError('Username may only contain letters, numbers, hyphens and underscores')
      return
    }

    if (!formData.password) {
      showError('Password is required')
      return
    }

    if (formData.password.length < VALIDATION.MIN_PASSWORD_LENGTH) {
      showError(`Password must be at least ${VALIDATION.MIN_PASSWORD_LENGTH} characters`)
      return
    }

    setSaving(true)

    try {
      const response = await fetch('/api/v1/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: formData.username.trim(),
          password: formData.password,
          role: formData.role,
        }),
      })

      const data: UsersResponse = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Could not create user')
      }

      showSuccess('User created')
      resetForm()
      fetchUsers()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not create user')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (user: User) => {
    if (!confirm(`Are you sure you want to delete the user "${user.username}"?`)) {
      return
    }

    try {
      const response = await fetch(`/api/v1/users/${user.id}`, {
        method: 'DELETE',
      })

      const data: UsersResponse = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Could not delete user')
      }

      showSuccess('User deleted')
      fetchUsers()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not delete user')
    }
  }

  const handleRoleChange = async (user: User, newRole: string) => {
    if (user.role === newRole) return

    try {
      const response = await fetch(`/api/v1/users/${user.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      })

      const data: UsersResponse = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Could not update user')
      }

      showSuccess('Role updated')
      fetchUsers()
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not update user')
    }
  }

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!resetPasswordUser) return

    if (!newPassword) {
      showError('Password is required')
      return
    }

    if (newPassword.length < VALIDATION.MIN_PASSWORD_LENGTH) {
      showError(`Password must be at least ${VALIDATION.MIN_PASSWORD_LENGTH} characters`)
      return
    }

    setResetting(true)

    try {
      const response = await fetch(`/api/v1/users/${resetPasswordUser.id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword }),
      })

      const data: UsersResponse = await response.json()

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Could not reset password')
      }

      showSuccess('Password reset')
      setResetPasswordUser(null)
      setNewPassword('')
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Could not reset password')
    } finally {
      setResetting(false)
    }
  }

  if (loading) {
    return (
      <div className="page users">
        <h1>Users</h1>
        <p>Loading users...</p>
      </div>
    )
  }

  return (
    <div className="page users">
      <h1>Users</h1>
      <p>Manage system users.</p>

      {message && (
        <div className={`message message--${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="users-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? 'Cancel' : 'Create new user'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="users-form">
          <div className="users-section">
            <h2>Create new user</h2>

            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                placeholder="e.g. john_doe"
                autoComplete="off"
                required
              />
              <span className="form-help">Only letters, numbers, hyphens and underscores</span>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                placeholder="At least 4 characters"
                autoComplete="new-password"
                required
              />
              <span className="form-help">User will be asked to change password on first login</span>
            </div>

            <div className="form-group">
              <label htmlFor="role">Role</label>
              <select
                id="role"
                value={formData.role}
                onChange={(e) => handleInputChange('role', e.target.value)}
                required
              >
                <option value="guest">Guest</option>
                <option value="admin">Admin</option>
              </select>
              <span className="form-help">Admins can create and delete users</span>
            </div>

            <div className="form-actions">
              <button type="button" className="btn" onClick={resetForm}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Creating...' : 'Create user'}
              </button>
            </div>
          </div>
        </form>
      )}

      <div className="users-section">
        <h2>Existing users</h2>
        {users.length === 0 ? (
          <p className="text-secondary">No users found.</p>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td className="user-username">{user.username}</td>
                    <td>
                      <select
                        className="role-select"
                        value={user.role}
                        onChange={(e) => handleRoleChange(user, e.target.value)}
                      >
                        <option value="guest">Guest</option>
                        <option value="admin">Admin</option>
                      </select>
                    </td>
                    <td className="user-date">{user.created_at ? formatDateFull(user.created_at) : '--'}</td>
                    <td className="user-actions">
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        onClick={() => setResetPasswordUser(user)}
                      >
                        Reset
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-small"
                        onClick={() => handleDelete(user)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {resetPasswordUser && (
        <div className="modal-overlay" onClick={() => { setResetPasswordUser(null); setNewPassword('') }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Reset password</h2>
            <p>Enter new password for <strong>{resetPasswordUser.username}</strong></p>
            <form onSubmit={handleResetPassword}>
              <div className="form-group">
                <label htmlFor="newPassword">New password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 4 characters"
                  autoComplete="new-password"
                  autoFocus
                  required
                />
                <span className="form-help">User will be asked to change password on next login</span>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => { setResetPasswordUser(null); setNewPassword('') }}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={resetting}>
                  {resetting ? 'Resetting...' : 'Reset password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
